import { useState } from 'react';
import {
  type MaintenanceEntry,
  type MaintenanceModule,
  type MaintenanceType,
  allModules,
  moduleMeta,
  allowedRolesOptions,
  globalIPs,
} from '@/mocks/saGlobalMaintenanceCenter';

interface MaintenanceFormModalProps {
  entry: MaintenanceEntry | null;
  onClose: () => void;
  onSave: (data: Partial<MaintenanceEntry>) => void;
}

export default function MaintenanceFormModal({ entry, onClose, onSave }: MaintenanceFormModalProps) {
  const isEditing = !!entry;

  const [formData, setFormData] = useState({
    name: entry?.name || '',
    description: entry?.description || '',
    type: (entry?.type || 'global') as MaintenanceType,
    modules: entry?.modules || ([] as MaintenanceModule[]),
    customMessage: entry?.customMessage || '',
    startDate: entry?.startDate ? entry.startDate.split('T')[0] : '',
    startTime: entry?.startDate ? entry.startDate.split('T')[1]?.substring(0, 5) : '',
    endDate: entry?.endDate ? entry.endDate.split('T')[0] : '',
    endTime: entry?.endDate ? entry.endDate.split('T')[1]?.substring(0, 5) : '',
    allowedIPs: entry?.allowedIPs || [],
    allowedUsers: entry?.allowedUsers?.join(', ') || '',
    allowedRoles: entry?.allowedRoles || [],
    customPageEnabled: entry?.customPageEnabled || false,
    customPageTheme: (entry?.customPageTheme || 'dark') as 'dark' | 'light',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const toggleModule = (md: MaintenanceModule) => {
    setFormData((prev) => {
      if (md === 'all') {
        return { ...prev, modules: prev.modules.includes('all') ? [] : ['all'] };
      }
      const modules = prev.modules.includes(md)
        ? prev.modules.filter((m) => m !== md && m !== 'all')
        : [...prev.modules.filter((m) => m !== 'all'), md];
      return { ...prev, modules };
    });
  };

  const toggleRole = (role: string) => {
    setFormData((prev) => ({
      ...prev,
      allowedRoles: prev.allowedRoles.includes(role)
        ? prev.allowedRoles.filter((r) => r !== role)
        : [...prev.allowedRoles, role],
    }));
  };

  const toggleIP = (ip: string) => {
    setFormData((prev) => ({
      ...prev,
      allowedIPs: prev.allowedIPs.includes(ip)
        ? prev.allowedIPs.filter((i) => i !== ip)
        : [...prev.allowedIPs, ip],
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'El nombre es requerido';
    if (!formData.description.trim()) newErrors.description = 'La descripción es requerida';
    if (!formData.startDate) newErrors.startDate = 'La fecha de inicio es requerida';
    if (!formData.endDate) newErrors.endDate = 'La fecha de finalización es requerida';
    if (formData.type === 'per_module' && formData.modules.length === 0) {
      newErrors.modules = 'Selecciona al menos un módulo';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const startFull = `${formData.startDate}T${formData.startTime || '00:00'}:00Z`;
    const endFull = `${formData.endDate}T${formData.endTime || '23:59'}:00Z`;
    const start = new Date(startFull);
    const end = new Date(endFull);
    const diffMs = end.getTime() - start.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const durationLabel = hours > 0
      ? `${hours} hora${hours > 1 ? 's' : ''}${mins > 0 ? ` ${mins} minutos` : ''}`
      : `${mins} minutos`;

    onSave({
      ...formData,
      startDate: startFull,
      endDate: endFull,
      customMessage: formData.customMessage || 'Plataforma en mantenimiento. Volveremos pronto.',
      allowedUsers: formData.allowedUsers
        ? formData.allowedUsers.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
      duration: durationLabel,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-2xl max-h-[90vh] border border-background-200 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-background-200 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-foreground-950 flex items-center justify-center">
              <i className={`${isEditing ? 'ri-edit-line' : 'ri-add-line'} text-background-50 text-lg`}></i>
            </div>
            <div>
              <h3 className="text-foreground-900 font-bold text-sm">
                {isEditing ? 'Editar Mantenimiento' : 'Nuevo Mantenimiento'}
              </h3>
              <p className="text-foreground-400 text-[10px]">Datos mock — preparado para integración backend</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl bg-background-200 flex items-center justify-center text-foreground-500 hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Form */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {/* Name */}
          <div>
            <label className="text-foreground-400 text-[10px] font-semibold block mb-1">Nombre *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              placeholder="Ej: Actualización de Infraestructura"
              className={`w-full px-3 py-2.5 rounded-xl bg-background-50 border text-foreground-800 text-xs outline-none transition-colors ${
                errors.name ? 'border-red-300 focus:border-red-400' : 'border-background-200 focus:border-background-400'
              }`}
            />
            {errors.name && <p className="text-red-500 text-[10px] mt-1">{errors.name}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="text-foreground-400 text-[10px] font-semibold block mb-1">Descripción *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
              placeholder="Describe el propósito del mantenimiento..."
              rows={2}
              className={`w-full px-3 py-2.5 rounded-xl bg-background-50 border text-foreground-800 text-xs outline-none resize-none transition-colors ${
                errors.description ? 'border-red-300 focus:border-red-400' : 'border-background-200 focus:border-background-400'
              }`}
            ></textarea>
            {errors.description && <p className="text-red-500 text-[10px] mt-1">{errors.description}</p>}
          </div>

          {/* Type */}
          <div>
            <label className="text-foreground-400 text-[10px] font-semibold block mb-2">Tipo de mantenimiento</label>
            <div className="flex items-center gap-2">
              {(['global', 'per_module'] as const).map((tp) => (
                <button
                  key={tp}
                  onClick={() => setFormData((p) => ({ ...p, type: tp, modules: tp === 'global' ? ['all'] : [] }))}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                    formData.type === tp
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-50 border border-background-200 text-foreground-600 hover:border-background-300'
                  }`}
                >
                  <i className={`${tp === 'global' ? 'ri-global-line' : 'ri-stack-line'} text-sm`}></i>
                  {tp === 'global' ? 'Global' : 'Por Módulo'}
                </button>
              ))}
            </div>
          </div>

          {/* Modules (only if per_module) */}
          {formData.type === 'per_module' && (
            <div>
              <label className="text-foreground-400 text-[10px] font-semibold block mb-2">Módulos afectados *</label>
              <div className="flex flex-wrap gap-1.5">
                {allModules.map((md) => {
                  const meta = moduleMeta[md];
                  const isSelected = formData.modules.includes(md);
                  return (
                    <button
                      key={md}
                      onClick={() => toggleModule(md)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-foreground-950 text-background-50'
                          : 'bg-background-50 border border-background-200 text-foreground-600 hover:border-background-300'
                      }`}
                    >
                      <i className={`${meta.icon} text-[10px]`}></i>
                      {meta.label}
                    </button>
                  );
                })}
              </div>
              {errors.modules && <p className="text-red-500 text-[10px] mt-1">{errors.modules}</p>}
            </div>
          )}

          {/* Date and time */}
          <div>
            <label className="text-foreground-400 text-[10px] font-semibold block mb-2">Ventana de mantenimiento</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-foreground-400 text-[9px] block mb-1">Fecha inicio *</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData((p) => ({ ...p, startDate: e.target.value }))}
                  className={`w-full px-2.5 py-2 rounded-xl bg-background-50 border text-foreground-700 text-xs outline-none cursor-pointer transition-colors ${
                    errors.startDate ? 'border-red-300' : 'border-background-200 focus:border-background-400'
                  }`}
                />
                {errors.startDate && <p className="text-red-500 text-[10px] mt-1">{errors.startDate}</p>}
              </div>
              <div>
                <label className="text-foreground-400 text-[9px] block mb-1">Hora inicio</label>
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData((p) => ({ ...p, startTime: e.target.value }))}
                  className="w-full px-2.5 py-2 rounded-xl bg-background-50 border border-background-200 text-foreground-700 text-xs outline-none cursor-pointer focus:border-background-400 transition-colors"
                />
              </div>
              <div>
                <label className="text-foreground-400 text-[9px] block mb-1">Fecha fin *</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData((p) => ({ ...p, endDate: e.target.value }))}
                  className={`w-full px-2.5 py-2 rounded-xl bg-background-50 border text-foreground-700 text-xs outline-none cursor-pointer transition-colors ${
                    errors.endDate ? 'border-red-300' : 'border-background-200 focus:border-background-400'
                  }`}
                />
                {errors.endDate && <p className="text-red-500 text-[10px] mt-1">{errors.endDate}</p>}
              </div>
              <div>
                <label className="text-foreground-400 text-[9px] block mb-1">Hora fin</label>
                <input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData((p) => ({ ...p, endTime: e.target.value }))}
                  className="w-full px-2.5 py-2 rounded-xl bg-background-50 border border-background-200 text-foreground-700 text-xs outline-none cursor-pointer focus:border-background-400 transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Custom message */}
          <div>
            <label className="text-foreground-400 text-[10px] font-semibold block mb-1">Mensaje personalizado</label>
            <textarea
              value={formData.customMessage}
              onChange={(e) => setFormData((p) => ({ ...p, customMessage: e.target.value }))}
              placeholder="Mensaje que verán los usuarios durante el mantenimiento..."
              rows={2}
              className="w-full px-3 py-2.5 rounded-xl bg-background-50 border border-background-200 text-foreground-800 text-xs outline-none resize-none focus:border-background-400 transition-colors"
            ></textarea>
          </div>

          {/* Custom page */}
          <div className="flex items-center justify-between bg-background-100 rounded-xl p-3">
            <div>
              <p className="text-foreground-800 text-xs font-semibold">Página de mantenimiento</p>
              <p className="text-foreground-400 text-[10px]">Mostrar página personalizada durante el mantenimiento</p>
            </div>
            <div className="flex items-center gap-2">
              {formData.customPageEnabled && (
                <button
                  onClick={() => setFormData((p) => ({ ...p, customPageTheme: p.customPageTheme === 'dark' ? 'light' : 'dark' }))}
                  className={`px-2 py-1 rounded-lg text-[10px] font-semibold cursor-pointer transition-colors ${
                    formData.customPageTheme === 'dark'
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-50 border border-background-200 text-foreground-600'
                  }`}
                >
                  {formData.customPageTheme === 'dark' ? 'Oscuro' : 'Claro'}
                </button>
              )}
              <button
                onClick={() => setFormData((p) => ({ ...p, customPageEnabled: !p.customPageEnabled }))}
                className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer ${
                  formData.customPageEnabled ? 'bg-emerald-500' : 'bg-foreground-300'
                }`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                  formData.customPageEnabled ? 'left-[18px]' : 'left-0.5'
                }`}></span>
              </button>
            </div>
          </div>

          {/* Allowed IPs */}
          <div>
            <label className="text-foreground-400 text-[10px] font-semibold block mb-2">IPs permitidas</label>
            <div className="flex flex-wrap gap-1.5">
              {globalIPs.map((ip) => {
                const isSelected = formData.allowedIPs.includes(ip);
                return (
                  <button
                    key={ip}
                    onClick={() => toggleIP(ip)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium font-mono transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 border border-background-200 text-foreground-500 hover:border-background-300'
                    }`}
                  >
                    {ip}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Allowed users */}
          <div>
            <label className="text-foreground-400 text-[10px] font-semibold block mb-1">Usuarios permitidos (email, separados por coma)</label>
            <input
              type="text"
              value={formData.allowedUsers}
              onChange={(e) => setFormData((p) => ({ ...p, allowedUsers: e.target.value }))}
              placeholder="carlos@readdy.ai, laura@readdy.ai"
              className="w-full px-3 py-2.5 rounded-xl bg-background-50 border border-background-200 text-foreground-800 text-xs outline-none focus:border-background-400 transition-colors"
            />
          </div>

          {/* Allowed roles */}
          <div>
            <label className="text-foreground-400 text-[10px] font-semibold block mb-2">Roles permitidos</label>
            <div className="flex flex-wrap gap-1.5">
              {allowedRolesOptions.map((role) => {
                const isSelected = formData.allowedRoles.includes(role);
                return (
                  <button
                    key={role}
                    onClick={() => toggleRole(role)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 border border-background-200 text-foreground-600 hover:border-background-300'
                    }`}
                  >
                    {role}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 p-5 border-t border-background-200 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 text-xs font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            {isEditing ? 'Guardar cambios' : 'Crear mantenimiento'}
          </button>
        </div>
      </div>
    </div>
  );
}